import * as Location from 'expo-location';
import { Platform } from 'react-native';

import { UserFacingError } from '@/src/utils/errors';

type BrowserReverseGeocodeResponse = {
  city?: string;
  countryCode?: string;
  countryName?: string;
  locality?: string;
  principalSubdivision?: string;
  principalSubdivisionCode?: string;
};

const browserReverseGeocodeEndpoint =
  'https://api.bigdatacloud.net/data/reverse-geocode-client';

function uniqueLocationParts(parts: string[]) {
  const seen = new Set<string>();

  return parts.filter((part) => {
    const normalized = part.trim();
    const key = normalized.toLowerCase();

    if (!normalized || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function formatProfileLocation(address: Location.LocationGeocodedAddress) {
  const locality =
    address.city ?? address.district ?? address.subregion ?? address.name;
  const parts = uniqueLocationParts([
    locality ?? '',
    address.region ?? '',
    address.country ?? '',
  ]);

  return parts.join(', ');
}

function stateCodeFromSubdivisionCode(value: string | undefined) {
  const match = value?.match(/^US-([A-Z]{2})$/);

  return match?.[1] ?? null;
}

function normalizeCountryName(value: string | undefined) {
  if (!value) {
    return '';
  }

  return value === 'United States of America (the)' ? 'United States' : value;
}

function formatBrowserProfileLocation(data: BrowserReverseGeocodeResponse) {
  const locality = data.city ?? data.locality;
  const region =
    stateCodeFromSubdivisionCode(data.principalSubdivisionCode) ??
    data.principalSubdivision;
  const parts = uniqueLocationParts([
    locality ?? '',
    region ?? '',
    normalizeCountryName(data.countryName),
  ]);

  return parts.join(', ');
}

function getBrowserCurrentPosition() {
  return new Promise<GeolocationPosition>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(
        new UserFacingError(
          'Browser location is not available. You can still type your city, region, or country.',
        ),
      );
      return;
    }

    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => {
        if (error.code === 1) {
          reject(
            new UserFacingError(
              'Location permission was not granted. You can still type your city, region, or country.',
            ),
          );
          return;
        }

        reject(
          new UserFacingError(
            'We could not read your browser location. You can still type your city, region, or country.',
          ),
        );
      },
      {
        enableHighAccuracy: false,
        maximumAge: 5 * 60 * 1000,
        timeout: 10000,
      },
    );
  });
}

async function getBrowserProfileLocation() {
  const currentLocation = await getBrowserCurrentPosition();
  const latitude = currentLocation.coords.latitude;
  const longitude = currentLocation.coords.longitude;

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new UserFacingError('We could not read your browser location.');
  }

  const params = new URLSearchParams({
    latitude: String(latitude),
    localityLanguage: 'en',
    longitude: String(longitude),
  });
  let response: Response;

  try {
    response = await fetch(`${browserReverseGeocodeEndpoint}?${params}`);
  } catch {
    throw new UserFacingError(
      'We could not look up a city or region for this location.',
    );
  }

  if (!response.ok) {
    throw new UserFacingError(
      'We could not look up a city or region for this location.',
    );
  }

  const data = (await response.json()) as BrowserReverseGeocodeResponse;
  const displayLocation = formatBrowserProfileLocation(data);

  if (!displayLocation) {
    throw new UserFacingError(
      'We could not format this location for your profile.',
    );
  }

  return displayLocation;
}

export async function getCurrentProfileLocation() {
  if (Platform.OS === 'web') {
    return getBrowserProfileLocation();
  }

  const permission = await Location.requestForegroundPermissionsAsync();

  if (permission.status !== Location.PermissionStatus.GRANTED) {
    throw new UserFacingError(
      'Location permission was not granted. You can still type your city, region, or country.',
    );
  }

  const currentLocation = await Location.getCurrentPositionAsync({
    accuracy: Location.Accuracy.Balanced,
  });
  const [address] = await Location.reverseGeocodeAsync({
    latitude: currentLocation.coords.latitude,
    longitude: currentLocation.coords.longitude,
  });

  if (!address) {
    throw new UserFacingError(
      'We could not find a city or region for this location.',
    );
  }

  const displayLocation = formatProfileLocation(address);

  if (!displayLocation) {
    throw new UserFacingError(
      'We could not format this location for your profile.',
    );
  }

  return displayLocation;
}
